import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatPhone } from "@/lib/phone";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/ui/rich-text";
import { Checkbox } from "@/components/ui/checkbox";
import { CircleNotch } from "@phosphor-icons/react";
import useUnsavedGuard from "@/hooks/useUnsavedGuard";

const EMPTY = {
  first_name: "", last_name: "", title: "", office_phone: "", mobile_phone: "",
  alternate_phone: "", email: "", secondary_email: "", linkedin_url: "", notes: "",
};

function cleaned(form, classifications) {
  const out = { classifications };
  Object.entries(form).forEach(([k, v]) => { out[k] = v === "" ? null : v; });
  out.first_name = form.first_name;
  out.last_name = form.last_name;
  return out;
}

export default function ContactDialog({ open, prospectId, contact, classificationOptions, onClose, onDone }) {
  // State is initialized once per mount; the parent remounts this dialog via
  // a `key` whenever the target contact changes, so no sync effect is needed.
  const [form, setForm] = useState(() => ({
    first_name: (contact && contact.first_name) || "",
    last_name: (contact && contact.last_name) || "",
    title: (contact && contact.title) || "",
    office_phone: (contact && contact.office_phone) || "",
    mobile_phone: (contact && contact.mobile_phone) || "",
    alternate_phone: (contact && contact.alternate_phone) || "",
    email: (contact && contact.email) || "",
    secondary_email: (contact && contact.secondary_email) || "",
    linkedin_url: (contact && contact.linkedin_url) || "",
    notes: (contact && contact.notes) || "",
    intelligence_html: (contact && contact.intelligence_html) || "",
  }));
  const [classifications, setClassifications] = useState(() => (contact && contact.classifications) || []);
  const [loading, setLoading] = useState(false);
  const guard = useUnsavedGuard([form, classifications]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleClassification = (name) => {
    setClassifications((prev) =>
      prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (contact) {
        await api.patch(`/contacts/${contact.id}`, cleaned(form, classifications));
        toast.success("Contact updated");
      } else {
        await api.post(`/prospects/${prospectId}/contacts`, cleaned(form, classifications));
        toast.success("Contact added");
      }
      onDone();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent {...guard.guardProps} className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-lg" data-testid="contact-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#032852]">
            {contact ? "Edit Contact" : "Add Contact"}
          </DialogTitle>
          <DialogDescription>A prospect may have multiple contacts; one is designated primary.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">First name *</Label>
              <Input required value={form.first_name} onChange={set("first_name")} className="rounded-sm" data-testid="contact-first-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Last name *</Label>
              <Input required value={form.last_name} onChange={set("last_name")} className="rounded-sm" data-testid="contact-last-name" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Job title / position</Label>
            <Input value={form.title} onChange={set("title")} className="rounded-sm" data-testid="contact-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Office phone</Label>
              <Input value={form.office_phone} onChange={set("office_phone")} onBlur={(e) => set("office_phone")({ target: { value: formatPhone(e.target.value) } })} className="rounded-sm" data-testid="contact-office-phone" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Mobile phone</Label>
              <Input value={form.mobile_phone} onChange={set("mobile_phone")} onBlur={(e) => set("mobile_phone")({ target: { value: formatPhone(e.target.value) } })} className="rounded-sm" data-testid="contact-mobile-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Alternate phone</Label>
              <Input value={form.alternate_phone} onChange={set("alternate_phone")} onBlur={(e) => set("alternate_phone")({ target: { value: formatPhone(e.target.value) } })} className="rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Email</Label>
              <Input type="email" value={form.email} onChange={set("email")} className="rounded-sm" data-testid="contact-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Secondary email</Label>
              <Input type="email" value={form.secondary_email} onChange={set("secondary_email")} className="rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">LinkedIn URL</Label>
              <Input value={form.linkedin_url} onChange={set("linkedin_url")} className="rounded-sm" placeholder="https://linkedin.com/in/..." />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Classifications</Label>
            <div className="grid grid-cols-2 gap-2 rounded-sm border border-[#E4E4E7] p-3 sm:grid-cols-3">
              {(classificationOptions || []).map((c) => (
                <label key={c.id} className="flex cursor-pointer items-center gap-2 text-xs text-[#52525B]">
                  <Checkbox
                    checked={classifications.includes(c.name)}
                    onCheckedChange={() => toggleClassification(c.name)}
                    data-testid={`classification-${c.name.replace(/[^a-z]/gi, "-").toLowerCase()}`}
                  />
                  {c.name}
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Contact notes</Label>
            <Textarea value={form.notes} onChange={set("notes")} className="rounded-sm" rows={2} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Contact Intelligence (optional)</Label>
            <RichTextEditor value={form.intelligence_html}
              onChange={(html) => setForm((f) => ({ ...f, intelligence_html: html }))}
              placeholder="Role in decisions, relationship history, preferences…"
              testId="contact-intel-editor" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="submit-contact">
              {loading ? <CircleNotch size={18} className="animate-spin" /> : contact ? "Save contact" : "Add contact"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
